/**
 * Centralized logging utility that respects environment settings.
 * 
 * Features:
 * - Environment-aware logging (disabled in production unless explicitly enabled)
 * - Categorized log levels (debug, info, warn, error)
 * - Namespaced loggers for different modules
 * - Consistent formatting with timestamps and module prefixes
 * 
 * @example
 * ```ts
 * import { logger, createLogger } from '@/shared/lib/logger';
 * 
 * // Use the default logger
 * logger.debug('Debug message');
 * logger.info('Info message');
 * 
 * // Create a namespaced logger
 * const authLogger = createLogger('Auth');
 * authLogger.info('User logged in');
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  /** Whether logging is enabled */
  enabled: boolean;
  /** Minimum log level to output */
  minLevel: LogLevel;
  /** Whether to include timestamps */
  showTimestamp: boolean;
}

/** Log level priority for filtering */
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Determines if we're in development mode.
 * In production, most logs are suppressed unless explicitly enabled.
 */
const isDev = import.meta.env.DEV;

/** Default configuration based on environment */
const defaultConfig: LoggerConfig = {
  enabled: isDev,
  minLevel: isDev ? 'debug' : 'warn',
  showTimestamp: isDev,
};

/**
 * Creates a logger instance with an optional namespace.
 * 
 * @param namespace - Optional prefix for all log messages (e.g., 'Auth', 'Network')
 * @param config - Optional configuration overrides
 * @returns Logger object with debug, info, warn, and error methods
 */
export const createLogger = (
  namespace?: string,
  config: Partial<LoggerConfig> = {}
) => {
  const finalConfig = { ...defaultConfig, ...config };

  const formatMessage = (level: LogLevel, message: string): string => {
    const parts: string[] = [];
    
    if (finalConfig.showTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }
    
    if (namespace) {
      parts.push(`[${namespace}]`);
    }
    
    parts.push(message);
    return parts.join(' ');
  };

  const shouldLog = (level: LogLevel): boolean => {
    if (!finalConfig.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[finalConfig.minLevel];
  };

  const log = (level: LogLevel, message: string, ...args: unknown[]): void => {
    if (!shouldLog(level)) return;

    const formattedMessage = formatMessage(level, message);
    
    switch (level) {
      case 'debug':
        console.debug(formattedMessage, ...args);
        break;
      case 'info':
        console.info(formattedMessage, ...args);
        break;
      case 'warn':
        console.warn(formattedMessage, ...args);
        break;
      case 'error':
        console.error(formattedMessage, ...args);
        break;
    }
  };

  return {
    /**
     * Log debug-level messages (development only)
     */
    debug: (message: string, ...args: unknown[]) => log('debug', message, ...args),
    
    /**
     * Log info-level messages (development only by default)
     */
    info: (message: string, ...args: unknown[]) => log('info', message, ...args),
    
    /**
     * Log warning-level messages
     */
    warn: (message: string, ...args: unknown[]) => log('warn', message, ...args),
    
    /**
     * Log error-level messages (always logged)
     */
    error: (message: string, ...args: unknown[]) => log('error', message, ...args),
    
    /**
     * Check if a specific log level is enabled
     */
    isEnabled: (level: LogLevel = 'debug') => shouldLog(level),
  };
};

/**
 * Default logger instance for general use.
 * For module-specific logging, use createLogger('ModuleName').
 */
export const logger = createLogger();

// Pre-configured loggers for common modules
export const authLogger = createLogger('Auth');
export const networkLogger = createLogger('Network');
export const analyticsLogger = createLogger('Analytics');
export const offlineLogger = createLogger('Offline');
export const voiceLogger = createLogger('Voice');
